import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  CircularProgress
} from '@mui/material'
import { ExpandMore } from '@mui/icons-material'
import { useMutation } from '@tanstack/react-query'
import { updateCreativePreview } from '../getData'
import {
  Zone,
  CreativePreviewInput,
  CreativePreviewResponse,
  Flight,
  CreativeUploadResponse,
  ContentCategory
} from '../../types/campaign'
import { OUTDATED_CREATIVE_WARNING } from '../../types/constants'
import theme from '../theme'
import FlightCreative from '../FlightCreative'
import { CampaignStatus } from '../../types/campaign'
import { formatPreviewInput } from '../utils'

interface Props {
  selectedStores: number[]
  availableZones: Zone[]
  isLoading: boolean
  isError: boolean
  onSelectionChange: (previewInput: CreativePreviewInput) => void
  savedZones: number[]
  savedPlacements: number[]
  saveTargetsMutation: () => void
  onOutdatedCreatives: (outdated: CreativeUploadResponse[]) => void
  campaignStatus: CampaignStatus
  flight?: Flight
  onPreviewZonesChange?: (zoneIds: number[]) => void
  onPreviewPlacementsChange?: (placementIds: number[]) => void
  onPreviewTargetsChange?: (data: {
    previewZones: number[]
    previewPlacements: number[]
  }) => void
  campaignContentCategory?: ContentCategory | null
  isGtvUser: boolean
}

const FlightZones = ({
  selectedStores,
  availableZones,
  isLoading,
  isError,
  onSelectionChange,
  flight,
  savedZones,
  savedPlacements,
  saveTargetsMutation,
  onOutdatedCreatives,
  campaignStatus,
  onPreviewZonesChange,
  onPreviewPlacementsChange,
  onPreviewTargetsChange,
  campaignContentCategory,
  isGtvUser
}: Props) => {
  const [selectedPlacements, setSelectedPlacements] = useState<number[]>([])
  const [creativePreview, setCreativePreview] =
    useState<CreativePreviewResponse | null>(null)
  const [warningMessage, setWarningMessage] = useState<string | undefined>(
    undefined
  )

  const {
    mutate: updateCreativePreviewMutation,
    isPending: isUpdatingPreview,
    isError: isErrorUpdatingPreview
  } = useMutation({
    mutationFn: updateCreativePreview,
    onSuccess: (data) => {
      setCreativePreview(data)

      const outdatedFormats = flight?.creatives.filter((creative) => {
        const asset =
          creative.assets.find((creativeAsset) => creativeAsset.type === 'video') ?? creative.assets[0]
        const orientation = asset?.aspect_ratio.orientation
        if (!orientation) return false
        return !data.aspect_ratios.some(
          (aspectRatio) => orientation === aspectRatio.orientation
        )
      })
      if (outdatedFormats && outdatedFormats.length > 0) {
        setWarningMessage(OUTDATED_CREATIVE_WARNING)

        const outdated: CreativeUploadResponse[] = []
        outdatedFormats.forEach((creative) => {
          outdated.push(creative)
        })
        onOutdatedCreatives(outdated)
      } else {
        setWarningMessage(undefined)
      }
    },
    onError: (error) => {
      console.error(error)
    }
  })

  useEffect(() => {
    if (availableZones.length > 0) {
      const placementsFromZones = savedZones.flatMap((zoneId) => {
        return (
          availableZones
            .find((zone) => zone.id === zoneId)
            ?.placements.map((placement) => placement.id) ?? []
        )
      })
      setSelectedPlacements(
        Array.from(new Set([...placementsFromZones, ...savedPlacements]))
      )

      // For filling in existing zones and placements, then getting available creative formats
      if (savedPlacements.length > 0 || savedZones.length > 0) {
        const previewInput = {
          stores: selectedStores.map((store) => ({ id: store })),
          placements: savedPlacements.map((placement) => ({ id: placement })),
          zones: savedZones.map((zone) => ({ id: zone }))
        }
        onSelectionChange(previewInput)
        updateCreativePreviewMutation(previewInput)
      }
    }
  }, [availableZones])

  const isZoneSelected = (zonePlacements: { id: number }[]) => {
    return zonePlacements.every((p) => selectedPlacements.includes(p.id))
  }

  const handlePreviewTargets = (newSelectedPlacements: number[]) => {
    const placementSet = new Set(newSelectedPlacements)
    const zoneIds: number[] = []

    // Collect fully-selected zones and remove their placements from the set
    for (const zone of availableZones) {
      const allSelected = zone.placements.every((p) =>
        placementSet.has(p.id)
      )

      if (allSelected) {
        zoneIds.push(zone.id)
        zone.placements.forEach((p) => placementSet.delete(p.id))
      }
    }

    const remainingPlacementIds = Array.from(placementSet)

    // Send to parent
    onPreviewTargetsChange?.({
      previewZones: zoneIds,
      previewPlacements: remainingPlacementIds
    })
  }

  const handlePlacementChange = (placementId: number, checked: boolean) => {
    const newSelectedPlacements = checked
      ? [...selectedPlacements, placementId]
      : selectedPlacements.filter((id) => id !== placementId)

    setSelectedPlacements(newSelectedPlacements)
    onPreviewPlacementsChange?.(newSelectedPlacements)

    const previewInput = formatPreviewInput(
      newSelectedPlacements,
      selectedStores,
      availableZones
    )
    if (newSelectedPlacements.length > 0) {
      updateCreativePreviewMutation(previewInput)
    }
    onSelectionChange(previewInput) // Sends input to parent component
    handlePreviewTargets(newSelectedPlacements)
  }
  const handleZoneChange = (zone: Zone, checked: boolean) => {
    const placementIds = zone.placements.map((p) => p.id)
    const newSelectedPlacements = checked
      ? [...selectedPlacements, ...placementIds]
      : selectedPlacements.filter((id) => !placementIds.includes(id))

    setSelectedPlacements(newSelectedPlacements)

    const newPreviewZones = availableZones
      .filter((z) =>
        z.placements.every((p) => newSelectedPlacements.includes(p.id))
      )

    onPreviewZonesChange?.(newPreviewZones.map((z) => z.id))

    const previewInput = formatPreviewInput(
      newSelectedPlacements,
      selectedStores,
      newPreviewZones
    )
    if (newSelectedPlacements.length > 0) {
      updateCreativePreviewMutation(previewInput)
    }
    onSelectionChange(previewInput) // Sends input to parent component
    handlePreviewTargets(newSelectedPlacements)
  }

  const header = (
    <Typography
      variant="body-8"
      sx={{ display: 'block', marginBottom: theme.space['24'] }}
    >
      Zones
    </Typography>
  )

  if (isLoading) {
    return (
      <Box>
        {header}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <CircularProgress sx={{ color: theme.palette['accent-500'] }} />
        </Box>
      </Box>
    )
  }

  if (isError || !flight) {
    return (
      <Box>
        {header}
        <Typography variant="body-11">Error loading zones</Typography>
      </Box>
    )
  }

  if (selectedStores.length === 0) {
    return (
      <Box>
        {header}
        <Box
          sx={{
            padding: theme.space['24'],
            backgroundColor: theme.palette['sage-50'],
            borderRadius: theme.borders['rounded-10']
          }}
        >
          <Typography
            variant="body-11"
            sx={{ color: theme.palette['sage-500'] }}
          >
            Select stores to see the available zones
          </Typography>
        </Box>
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ marginTop: theme.space['32'] }}>
        {header}
        <Box sx={{ borderBottom: `1px solid ${theme.palette['sage-100']}` }}>
          <Box
            sx={{
              display: 'flex',
              marginBottom: theme.space['16']
            }}
          >
            <Box sx={{ width: '42px' }}>{/* Space for checkbox column */}</Box>
            <Typography
              variant="body-2"
              sx={{
                flex: 1,
                textTransform: 'uppercase'
              }}
            ></Typography>
            <Typography
              variant="body-2"
              sx={{ flex: 1, textTransform: 'uppercase' }}
            >
              # of Placements
            </Typography>
            <Typography
              variant="body-2"
              sx={{ flex: 1, textTransform: 'uppercase' }}
            >
              Stores
            </Typography>
            <Typography
              variant="body-2"
              sx={{ flex: 1, textTransform: 'uppercase' }}
            >
              Screens
            </Typography>
            <Box sx={{ width: '24px' }}>
              {/* Space for expand icon column */}
            </Box>
          </Box>
          {availableZones.map((zone) => {
            const selectedPlacementCount = zone.placements.filter((p) =>
              selectedPlacements.includes(p.id)
            ).length
            return (
              <Accordion
                key={zone.id}
                sx={{
                  boxShadow: 'none',
                  '&.Mui-expanded': {
                    margin: 0
                  }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{
                    padding: 0,
                    height: '52px',
                    '&.Mui-expanded': {
                      minHeight: 'auto',
                      borderTop: `1px solid ${theme.palette['sage-100']}`,
                      borderBottom: `1px solid ${theme.palette['sage-100']}`
                    }
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%'
                    }}
                  >
                    <Checkbox
                      checked={zone.placements.every((p) =>
                        selectedPlacements.includes(p.id)
                      )}
                      indeterminate={zone.placements.some(
                        (p) =>
                          selectedPlacements.includes(p.id) &&
                          !isZoneSelected(zone.placements)
                      )}
                      onChange={(e) => handleZoneChange(zone, e.target.checked)}
                      onClick={(e) => e.stopPropagation()}
                      inputProps={{
                        'aria-label': `Select ${zone.name}`
                      }}
                    />
                    <Typography variant="body-10" sx={{ flex: 1 }}>
                      {zone.name}
                    </Typography>
                    <Typography variant="body-10" sx={{ flex: 1 }}>
                      {`${selectedPlacementCount}/${zone.placements.length}`}
                    </Typography>
                    <Typography variant="body-10" sx={{ flex: 1 }}>
                      {zone.store_count}
                    </Typography>
                    <Typography variant="body-10" sx={{ flex: 1 }}>
                      {zone.screen_count}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 0 }}>
                  {zone.placements.map((placement) => (
                    <Box
                      key={placement.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        p: 2,
                        borderBottom: `1px solid ${theme.palette['sage-100']}`,
                        height: '52px',
                        '&:last-child': {
                          borderBottom: 'none'
                        }
                      }}
                    >
                      <Checkbox
                        checked={selectedPlacements.includes(placement.id)}
                        onChange={(e) =>
                          handlePlacementChange(placement.id, e.target.checked)
                        }
                      />
                      <Typography variant="body-10" sx={{ flex: 1 }}>
                        {placement.name}
                      </Typography>
                      <Typography
                        variant="body-10"
                        sx={{ flex: 1 }}
                      ></Typography>
                      <Typography
                        variant="body-10"
                        sx={{ flex: 1 }}
                      ></Typography>
                    </Box>
                  ))}
                </AccordionDetails>
              </Accordion>
            )
          })}
        </Box>
      </Box>
      <FlightCreative
        creativeFormats={creativePreview}
        creativeAssets={flight.creatives}
        isPending={isUpdatingPreview}
        isError={isErrorUpdatingPreview}
        flight={flight}
        isFlightAccordion={false}
        saveTargetsMutation={saveTargetsMutation}
        campaignStatus={campaignStatus}
        warningMessage={warningMessage}
        campaignContentCategory={campaignContentCategory}
        isGtvUser={isGtvUser}
      />
    </Box>
  )
}

export default FlightZones
